import React from 'react';
import { Button } from '../../../ui/button';
import { Input } from '../../../ui/input';
import { Textarea } from '../../../ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '../../../ui/card';
import { Label } from '../../../ui/label';
import { Loader2 } from 'lucide-react';
import { useAuth } from '../../../../lib/auth/AuthContext';
import { Id } from '../../../../convex/_generated/dataModel';
import { LegalInfoContent, FileId } from '../../../../types/pageBuilder';
import { DEFAULT_LEGAL_INFO_CONTENT } from '../../../../constants/pageBuilderDefaults';
import { useToast } from '../../../ui/use-toast';
import { ImageUploader } from '../../../ui/image-uploader';

interface LegalInfoSectionProps {
  projectId: string;
  selectedLanguage: string;
  legalInfoComponent: any;
  legalInfoContent: LegalInfoContent;
  setLegalInfoContent: React.Dispatch<React.SetStateAction<LegalInfoContent>>;
  isLoading: boolean;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  refreshComponents: () => void;
}

export const LegalInfoSection: React.FC<LegalInfoSectionProps> = ({
  projectId,
  selectedLanguage,
  legalInfoComponent,
  legalInfoContent,
  setLegalInfoContent,
  isLoading,
  setIsLoading,
  refreshComponents
}) => {
  const { useAuthenticatedMutation } = useAuth();
  const { toast } = useToast();
  const saveComponent = useAuthenticatedMutation('pageComponents:upsert');

  const handleSaveLegalInfoComponent = async () => {
    if (!projectId || !selectedLanguage) {
      toast({
        title: 'Error',
        description: 'Project ID and language are required'
      });
      return;
    }

    console.log('Saving legal info component with data:', legalInfoContent);
    setIsLoading(true);
    
    try {
      const componentId = await saveComponent({
        projectId: projectId as Id<"projects">,
        languageCode: selectedLanguage,
        componentType: 'legalInfo',
        order: 800, // Last component
        content: legalInfoContent,
        isActive: true,
        componentId: legalInfoComponent?._id
      });
      
      console.log('Legal info component saved with ID:', componentId);
      toast({
        title: 'Success',
        description: 'Legal information section saved successfully'
      });
      
      // Refresh the component list without changing the active tab
      refreshComponents();
    } catch (error) {
      console.error('Error saving legal info component:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to save legal info'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleQrCodeFileChange = (fileId: FileId | null, imageUrl: string) => {
    setLegalInfoContent(prev => ({
      ...prev,
      qrCodeUrl: imageUrl,
      qrCodeFileId: fileId || undefined
    }));
  };

  const resetToDefault = () => {
    setLegalInfoContent(DEFAULT_LEGAL_INFO_CONTENT);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Legal Info</CardTitle>
        <CardDescription>
          Add legal information about the project
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="dld-permit-number">DLD Permit Number</Label>
          <Input
            id="dld-permit-number"
            value={legalInfoContent.dldPermitNumber}
            onChange={(e) => setLegalInfoContent(prev => ({ ...prev, dldPermitNumber: e.target.value }))}
            placeholder="Enter DLD permit number"
          />
        </div>
        
        <div className="space-y-2">
          <Label>QR Code Image</Label>
          <ImageUploader
            projectId={projectId as Id<"projects">}
            purpose="qr-code"
            altText="DLD Verification QR Code"
            onUploadComplete={handleQrCodeFileChange}
            maxSizeMB={10}
            acceptedTypes={['image/jpeg', 'image/png', 'image/webp', 'image/svg+xml']}
            existingImageUrl={legalInfoContent.qrCodeUrl}
            existingFileId={legalInfoContent.qrCodeFileId}
            enableCompression={true}
            compressionThresholdKB={100}
          />
          <p className="text-sm text-gray-500">
            Upload a QR code image for DLD verification. Supported formats: JPG, PNG, WebP, SVG
          </p>
        </div>
      </CardContent>
      <CardFooter className="flex justify-between">
        <Button
          variant="outline"
          onClick={resetToDefault}
          disabled={isLoading}
        >
          Reset
        </Button>
        <Button 
          onClick={handleSaveLegalInfoComponent}
          disabled={isLoading}
        >
          {isLoading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Saving...
            </>
          ) : 'Save Legal Info'}
        </Button>
      </CardFooter>
    </Card>
  );
}; 